
import { useState } from 'react';
import BackendLayout from '../../components/feature/BackendLayout';
import PartnerDetail from './components/PartnerDetail';
import { mockPartners } from '../../mocks/partners';

export default function PartnersPage() {
  const [selectedPartner, setSelectedPartner] = useState<typeof mockPartners[0] | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'paused'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = mockPartners.filter((p) => {
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    const matchesSearch =
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const totalEarned = mockPartners.reduce((s, p) => s + p.totalEarned, 0);
  const activeCount = mockPartners.filter((p) => p.status === 'active').length;

  return (
    <BackendLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Partners</h1>
          <p className="text-sm text-gray-500">Manage all your affiliate partnerships, contracts &amp; credentials</p>
        </div>
        <button className="px-5 py-2.5 bg-[#0A0A0A] text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
          <i className="ri-add-line"></i> Add Partner
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-hand-coin-line text-xl text-emerald-600"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">${totalEarned.toLocaleString()}</div>
              <div className="text-xs text-gray-500">Total Earned</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-team-line text-xl text-teal-600"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">{mockPartners.length}</div>
              <div className="text-xs text-gray-500">Total Partners</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-circle-line text-xl text-sky-600"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">{activeCount}</div>
              <div className="text-xs text-gray-500">Active Contracts</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-refresh-line text-xl text-amber-600"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">
                {mockPartners.filter((p) => p.commissionType === 'Recurring').length}
              </div>
              <div className="text-xs text-gray-500">Recurring Deals</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between mb-5 gap-4">
        <div className="flex items-center gap-2">
          {(['all', 'active', 'paused'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer capitalize ${
                filterStatus === status
                  ? 'bg-[#0A0A0A] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            type="text"
            placeholder="Search partners..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/10 focus:border-gray-300 w-64"
          />
        </div>
      </div>

      {/* Partners Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Partner</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Commission</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Negotiated Price</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Earned</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contract</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((partner) => (
                <tr key={partner.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 ${partner.logoBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <i className={`${partner.logo} text-lg ${partner.logoColor}`}></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{partner.name}</div>
                        <div className="text-xs text-gray-400">{partner.category}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="text-sm font-semibold text-gray-900">{partner.commissionRate}</div>
                    <div className="text-xs text-gray-400">{partner.commissionType}</div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="text-sm font-medium text-gray-900">{partner.negotiatedPrice}</div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm font-bold text-emerald-600">${partner.totalEarned.toLocaleString()}</span>
                  </td>
                  <td className="px-5 py-4">
                    {partner.contractFile ? (
                      <div className="flex items-center gap-1.5">
                        <i className="ri-file-pdf-2-line text-red-500 text-sm"></i>
                        <span className="text-xs text-gray-600 truncate max-w-[120px]">{partner.contractFile}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">No file</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                      partner.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                    }`}>
                      {partner.status}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setSelectedPartner(partner)}
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-all cursor-pointer"
                        title="View Details"
                      >
                        <i className="ri-eye-line text-sm"></i>
                      </button>
                      <a
                        href={partner.affiliatePortalUrl}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-all cursor-pointer"
                        title="Open Portal"
                      >
                        <i className="ri-external-link-line text-sm"></i>
                      </a>
                      <button
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-all cursor-pointer"
                        title="Edit"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <div className="py-16 text-center">
            <i className="ri-search-line text-4xl text-gray-300 mb-3 block"></i>
            <div className="text-sm text-gray-500">No partners found</div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedPartner && (
        <PartnerDetail
          partner={selectedPartner}
          onClose={() => setSelectedPartner(null)}
        />
      )}
    </BackendLayout>
  );
}
